import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Plus, Pencil, Trash2, UserCheck, UserX, KeyRound,
  Loader2, Search, MapPin, Layers,
} from 'lucide-react';
import { BACKEND_URL } from '../lib/config';

// ── Multi-select component using native checkboxes ────────────────────────────
function MultiSelectList({ label, icon: Icon, items, selectedIds, onChange, emptyText }) {
  const toggle = (id) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter(x => x !== id)
        : [...selectedIds, id]
    );
  };

  return (
    <div>
      <Label className="flex items-center gap-1.5 mb-2 text-sm font-medium">
        {Icon && <Icon size={13} className="text-muted-foreground" />}
        {label}
      </Label>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground border border-dashed border-border/60 rounded-lg p-3 text-center">
          {emptyText}
        </p>
      ) : (
        <div
          className="max-h-[130px] overflow-y-auto border border-border/60 rounded-lg bg-[#0D1117] p-2 space-y-0.5"
          data-testid={`multiselect-${label.replace(/\s+/g, '-').toLowerCase()}`}
        >
          {items.map(item => (
            <label
              key={item.id}
              htmlFor={`chk-${item.id}`}
              className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-secondary/60 cursor-pointer transition-colors select-none"
            >
              <input
                id={`chk-${item.id}`}
                type="checkbox"
                data-testid={`checkbox-${item.id}`}
                checked={selectedIds.includes(item.id)}
                onChange={() => toggle(item.id)}
                className="w-4 h-4 rounded cursor-pointer"
                style={{ accentColor: '#2BB5D8' }}
              />
              <span className="text-sm leading-none flex-1">{item.name}</span>
              {item.location_name && (
                <span className="text-[10px] text-muted-foreground">{item.location_name}</span>
              )}
            </label>
          ))}
        </div>
      )}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {selectedIds.map(id => {
            const item = items.find(i => i.id === id);
            return item ? (
              <Badge
                key={id}
                variant="outline"
                className="text-[10px] border-[#2BB5D8]/30 text-[#2BB5D8] cursor-pointer"
                onClick={() => toggle(id)}
              >
                {item.name} ×
              </Badge>
            ) : null;
          })}
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function CoachManagement() {
  const [coaches, setCoaches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(null); // 'create' | 'edit' | 'reset'
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', password: '',
    location_ids: [], batch_ids: [],
  });
  const [resetForm, setResetForm] = useState({ email: '', new_password: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // BACKEND_URL, axios, all set* are stable module/useState refs — empty deps is correct.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, l, b] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/coaches`),
        axios.get(`${BACKEND_URL}/api/locations`),
        axios.get(`${BACKEND_URL}/api/batches`),
      ]);
      setCoaches(c.data);
      setLocations(l.data);
      setBatches(b.data);
    } catch (e) {
      console.error('Failed to load coach management data:', e);
      setError(e?.response?.data?.detail || 'Failed to load data. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Batches filtered to only those belonging to currently-selected locations
  const filteredBatches = form.location_ids.length
    ? batches.filter(b => {
        const selectedLocs = locations.filter(l => form.location_ids.includes(l.id));
        return selectedLocs.some(l => (l.batch_ids || []).includes(b.id));
      })
    : batches;

  // Enrich batches with location name for display
  const enrichedBatches = filteredBatches.map(b => ({
    ...b,
    location_name: locations
      .filter(l => form.location_ids.includes(l.id) && (l.batch_ids || []).includes(b.id))
      .map(l => l.name)
      .join(', '),
  }));

  const filtered = coaches.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setForm({ name: '', email: '', password: '', location_ids: [], batch_ids: [] });
    setError(''); setSuccess('');
    setDialog('create');
  };

  const openEdit = (coach) => {
    setSelected(coach);
    setForm({
      name: coach.name,
      email: coach.email,
      password: '',
      location_ids: coach.location_ids || (coach.location_id ? [coach.location_id] : []),
      batch_ids: coach.batch_ids || [],
    });
    setError(''); setSuccess('');
    setDialog('edit');
  };

  const openReset = (coach) => {
    setSelected(coach);
    setResetForm({ email: coach.email, new_password: '' });
    setError(''); setSuccess('');
    setDialog('reset');
  };

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      setError('Name, email and password are required');
      return;
    }
    setSaving(true); setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/admin/coaches`, {
        name: form.name,
        email: form.email,
        password: form.password,
        role: 'coach',
        location_ids: form.location_ids,
        batch_ids: form.batch_ids,
      });
      setSuccess('Coach created successfully');
      load();
      setTimeout(() => setDialog(null), 1200);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to create coach');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    setSaving(true); setError('');
    try {
      const payload = {
        name: form.name,
        email: form.email,
        location_ids: form.location_ids,
        batch_ids: form.batch_ids,
      };
      if (form.password) payload.password = form.password;
      await axios.put(`${BACKEND_URL}/api/admin/coaches/${selected.id}`, payload);
      setSuccess('Coach updated');
      load();
      setTimeout(() => setDialog(null), 1000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (coach) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/coaches/${coach.id}`, {
        is_active: !coach.is_active,
      });
      load();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coach? This cannot be undone.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/coaches/${id}`);
      load();
    } catch (e) { console.error(e); }
  };

  const handleReset = async () => {
    if (!resetForm.new_password) { setError('New password required'); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, resetForm);
      setSuccess('Password reset successfully');
      setTimeout(() => setDialog(null), 1200);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Coaches</h1>
            <p className="text-sm text-muted-foreground">Manage academy coaches</p>
          </div>
          <Button
            data-testid="add-coach-btn"
            onClick={openCreate}
            className="bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold"
          >
            <Plus size={16} className="mr-1" /> Add Coach
          </Button>
        </div>

        {/* Page-level error (only for load failures, not form errors) */}
        {error && !dialog && (
          <Alert variant="destructive" className="bg-destructive/10 border-destructive/30">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Card className="bg-[#111827] border-border/60">
          <CardHeader className="pb-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search coaches..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 bg-secondary/50 border-border/60 h-9"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={`skeleton-${i}`} className="h-14 bg-secondary/50 rounded animate-pulse" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <p>No coaches found.</p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 text-[#2BB5D8]"
                  onClick={openCreate}
                >
                  <Plus size={13} className="mr-1" /> Add First Coach
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border/60">
                      <th className="text-left text-xs text-muted-foreground font-medium py-3 px-4">Name</th>
                      <th className="text-left text-xs text-muted-foreground font-medium py-3 px-4 hidden sm:table-cell">Email</th>
                      <th className="text-left text-xs text-muted-foreground font-medium py-3 px-4 hidden md:table-cell">Locations</th>
                      <th className="text-left text-xs text-muted-foreground font-medium py-3 px-4">Status</th>
                      <th className="text-right text-xs text-muted-foreground font-medium py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {filtered.map(coach => {
                      // Support both old location_id (string) and new location_ids (array)
                      const coachLocationIds = coach.location_ids?.length
                        ? coach.location_ids
                        : (coach.location_id ? [coach.location_id] : []);
                      const coachLocNames = coachLocationIds
                        .map(id => locations.find(l => l.id === id)?.name)
                        .filter(Boolean);

                      return (
                        <tr key={coach.id} className="hover:bg-secondary/30">
                          <td className="py-3 px-4">
                            <div>
                              <p className="text-sm font-medium">{coach.name}</p>
                              <p className="text-xs text-muted-foreground sm:hidden">{coach.email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground hidden sm:table-cell">
                            {coach.email}
                          </td>
                          <td className="py-3 px-4 hidden md:table-cell">
                            {coachLocNames.length ? (
                              <div className="flex flex-wrap gap-1">
                                {coachLocNames.map(n => (
                                  <Badge
                                    key={n}
                                    variant="outline"
                                    className="text-[10px] border-[#2BB5D8]/30 text-[#2BB5D8]"
                                  >
                                    {n}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">–</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <Badge
                              variant="outline"
                              className={coach.is_active
                                ? 'border-green-500/30 text-green-400'
                                : 'border-destructive/30 text-destructive'}
                            >
                              {coach.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1 justify-end">
                              <Button
                                variant="ghost" size="icon" className="h-8 w-8"
                                onClick={() => openEdit(coach)}
                                title="Edit"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                variant="ghost" size="icon" className="h-8 w-8"
                                onClick={() => openReset(coach)}
                                title="Reset password"
                              >
                                <KeyRound size={14} />
                              </Button>
                              <Button
                                variant="ghost" size="icon" className="h-8 w-8"
                                onClick={() => toggleActive(coach)}
                                title={coach.is_active ? 'Deactivate' : 'Activate'}
                              >
                                {coach.is_active
                                  ? <UserX size={14} className="text-orange-400" />
                                  : <UserCheck size={14} className="text-green-400" />}
                              </Button>
                              <Button
                                variant="ghost" size="icon" className="h-8 w-8 text-destructive/70 hover:text-destructive"
                                onClick={() => handleDelete(coach.id)}
                                title="Delete"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Create / Edit Coach Dialog ─────────────────────────────────────── */}
        <Dialog open={dialog === 'create' || dialog === 'edit'} onOpenChange={v => !v && setDialog(null)}>
          <DialogContent className="bg-[#111827] border-border/60 max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {dialog === 'create' ? 'Add New Coach' : `Edit Coach – ${selected?.name}`}
              </DialogTitle>
            </DialogHeader>

            {error && (
              <Alert variant="destructive" className="bg-destructive/10 border-destructive/30">
                <AlertDescription data-testid="coach-form-error">{error}</AlertDescription>
              </Alert>
            )}
            {success && (
              <Alert className="bg-green-900/30 border-green-700/30 text-green-400">
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4 mt-1">
              {/* Name */}
              <div>
                <Label>Full Name</Label>
                <Input
                  data-testid="coach-name-input"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  className="mt-1 bg-secondary/50 border-border/60"
                  placeholder="Coach Name"
                />
              </div>

              {/* Email */}
              <div>
                <Label>Email</Label>
                <Input
                  data-testid="coach-email-input"
                  type="email"
                  value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  className="mt-1 bg-secondary/50 border-border/60"
                  placeholder="coach@email.com"
                />
              </div>

              {/* Password */}
              <div>
                <Label>
                  {dialog === 'edit' ? 'New Password (leave blank to keep)' : 'Password'}
                </Label>
                <Input
                  data-testid="coach-password-input"
                  type="password"
                  value={form.password}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  className="mt-1 bg-secondary/50 border-border/60"
                  placeholder={dialog === 'edit' ? 'Leave blank to keep current' : 'Set password'}
                />
              </div>

              {/* Locations (multi-select) */}
              <MultiSelectList
                label="Assign Locations"
                icon={MapPin}
                items={locations}
                selectedIds={form.location_ids}
                onChange={loc_ids => setForm(p => ({
                  ...p,
                  location_ids: loc_ids,
                  // Keep only batches that belong to at least one still-selected location
                  batch_ids: p.batch_ids.filter(bid => {
                    const selectedLocs = locations.filter(l => loc_ids.includes(l.id));
                    return selectedLocs.some(l => (l.batch_ids || []).includes(bid));
                  }),
                }))}
                emptyText="No locations found. Create locations first."
              />

              {/* Batches (multi-select, filtered by selected locations) */}
              <MultiSelectList
                label={`Assign Batches${form.location_ids.length ? ` (from ${form.location_ids.length} location${form.location_ids.length > 1 ? 's' : ''})` : ''}`}
                icon={Layers}
                items={enrichedBatches}
                selectedIds={form.batch_ids}
                onChange={batch_ids => setForm(p => ({ ...p, batch_ids }))}
                emptyText={
                  form.location_ids.length
                    ? 'No batches in selected locations.'
                    : 'Select locations first to see available batches.'
                }
              />

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setDialog(null)}>
                  Cancel
                </Button>
                <Button
                  data-testid="coach-submit-btn"
                  className="flex-1 bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold"
                  onClick={dialog === 'create' ? handleCreate : handleEdit}
                  disabled={saving}
                >
                  {saving && <Loader2 size={14} className="animate-spin mr-1" />}
                  {dialog === 'create' ? 'Create Coach' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ── Reset Password Dialog ─────────────────────────────────────────── */}
        <Dialog open={dialog === 'reset'} onOpenChange={v => !v && setDialog(null)}>
          <DialogContent className="bg-[#111827] border-border/60 max-w-md">
            <DialogHeader>
              <DialogTitle>Reset Password – {selected?.name}</DialogTitle>
            </DialogHeader>
            {error && (
              <Alert variant="destructive" className="bg-destructive/10 border-destructive/30">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {success && (
              <Alert className="bg-green-900/30 border-green-700/30 text-green-400">
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-4">
              <div>
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={resetForm.new_password}
                  onChange={e => setResetForm(p => ({ ...p, new_password: e.target.value }))}
                  className="mt-1 bg-secondary/50 border-border/60"
                  placeholder="Enter new password"
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setDialog(null)}>
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-[#F7941D] hover:bg-[#e08519] text-white font-semibold"
                  onClick={handleReset}
                  disabled={saving}
                >
                  {saving && <Loader2 size={14} className="animate-spin mr-1" />}
                  Reset Password
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
